import { useEffect, useMemo, useState } from 'react'
import {
  Spinner,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Combobox,
  Option,
  Textarea,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  tokens,
} from '@fluentui/react-components'
import { apiFetchJson } from '../lib/apiFetch'
import { useProblemFilters } from '../hooks/useProblemFilters'
import { buildRows, hasDuplicateLicense, isMissingOwner, isNotSso, isServiceBlocked, isStale } from '../lib/personRows'
import Lozenge from './Lozenge'
import ResolutionBadge from './ResolutionBadge'
import ProblemFilterBar from './ProblemFilterBar'
import OverrideExtraFields from './OverrideExtraFields'
import type { LicensesResponse, PersonLinkRequest, ServiceConfigResponse } from '../types'

const DEFAULT_STALE_LOGIN_DAYS = 90

type Mode = 'link' | 'external' | 'shared'

/** Row-button tooltip + dialog copy for each mode — the three actions look similar (all end up
 * calling the same POST) but resolve fundamentally different situations, so this is the one place
 * that spells out the distinction instead of leaving it to the reader to infer from button labels. */
const MODE_INFO: Record<Mode, { buttonHint: string; title: string; description: string; confirmLabel: string }> = {
  link: {
    buttonHint: 'This raw email actually belongs to a specific person already known in Entra — e.g. a personal alias or an old address. Attributing it here resolves it to that person, same as an automatic Entra match.',
    title: 'Link to a known person',
    description:
      'Attribute this email to a specific person already in Entra ID — use this when the address itself isn\'t their primary Entra address, but the account genuinely belongs to them.',
    confirmLabel: 'Link',
  },
  external: {
    buttonHint: 'This is a real, known non-employee (customer, partner, vendor) — not someone missing from Entra by mistake. Marking it external removes it from the "unresolved" list permanently.',
    title: 'Mark as external',
    description:
      'Declare this a known external contact rather than an unresolved account. Unlike "Link", there\'s no Entra person behind it — the comment you leave here is the record of that decision.',
    confirmLabel: 'Confirm',
  },
  shared: {
    buttonHint: 'This account isn\'t used by one person at all — a shared mailbox, a device account, a departmental resource. Naming a department or a responsible person here keeps it "owned" even without an individual.',
    title: 'Mark as shared / departmental',
    description:
      'Declare this a shared or departmental account (e.g. a shared inbox, a device account) with no single individual owner. Name a department and/or a responsible person so it still counts as owned.',
    confirmLabel: 'Confirm',
  },
}

/** Button hint for the combined External/Shared action — External and Shared use the exact same
 * input form (department/responsible/comment), differing only in which label/intent applies, so
 * they share one button and one dialog with a toggle at the top instead of two near-identical
 * buttons that were easy to confuse. "Link" stays separate since it actually attributes the
 * account to a real Entra person, a fundamentally different action. */
const EXTERNAL_OR_SHARED_BUTTON_HINT =
  'This account isn\'t used by one individual — either a real external contact (customer, partner, vendor) or a shared/departmental resource (shared mailbox, device account). Choose which in the dialog.'

export default function UserLinksTab(props: { serviceId: string; refreshKey: number; canManage: boolean }) {
  const { serviceId, refreshKey, canManage } = props
  const [data, setData] = useState<LicensesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [staleLoginDays, setStaleLoginDays] = useState(DEFAULT_STALE_LOGIN_DAYS)
  const [searchText, setSearchText] = useState('')
  const [editingEmail, setEditingEmail] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode | null>(null)
  const [query, setQuery] = useState('')
  const [selectedPersonEmail, setSelectedPersonEmail] = useState('')
  const [responsibleQuery, setResponsibleQuery] = useState('')
  const [selectedResponsibleEmail, setSelectedResponsibleEmail] = useState('')
  const [department, setDepartment] = useState('')
  const [comment, setComment] = useState('')

  function load() {
    apiFetchJson<LicensesResponse>(`/api/licenses?serviceId=${serviceId}`)
      .then(setData)
      .catch(e => setError(String(e)))
  }

  useEffect(load, [serviceId, refreshKey])

  useEffect(() => {
    apiFetchJson<ServiceConfigResponse>('/api/service-config')
      .then(res => setStaleLoginDays(res.config[serviceId]?.staleLoginDays ?? DEFAULT_STALE_LOGIN_DAYS))
      .catch(() => {})
  }, [serviceId])

  // groupByRawEmail: true — every override action (Link/Mark/Clear) is keyed by the raw
  // service-reported email, not the resolved person, and a manually-linked account must keep
  // showing as itself here (with a "Manually linked" badge), not disappear into its target's row.
  const rows = useMemo(
    () => (data ? buildRows(data.persons, data.results, { groupByRawEmail: true }).filter(row => row.byService[serviceId]?.length) : []),
    [data, serviceId],
  )
  const { problemFilters, problemFilter, setProblemFilter, roleFilter, setRoleFilter, activeFilter, hasRoles, duplicateKeys } =
    useProblemFilters(rows, staleLoginDays)
  const visibleRows = useMemo(() => {
    const text = searchText.trim().toLowerCase()
    return rows
      .filter(activeFilter.predicate)
      .filter(row => !roleFilter || row.role === roleFilter)
      .filter(row => !text || row.rawEmail.toLowerCase().includes(text) || row.personDisplayName.toLowerCase().includes(text))
  }, [rows, activeFilter, roleFilter, searchText])
  const persons = useMemo(() => data?.persons ?? [], [data])
  // Only from *enabled* persons — a disabled/departed person's leftover Entra department string
  // is nothing worth offering here. This does NOT dedupe genuine Entra inconsistency (e.g. "BO"
  // vs "Business Operations", or resource-account artifacts like "Microsoft Communication
  // Application Instance") — that's real, live data in Entra, not something StackRadar invents;
  // the picker is deliberately freeform so a manual override can still normalize it if needed.
  const departments = useMemo(
    () =>
      [...new Set(persons.filter(p => p.entraStatus === 'enabled').map(p => p.department).filter((d): d is string => !!d))].sort(),
    [persons],
  )
  const filteredPersons = query
    ? persons.filter(
        p =>
          p.displayName.toLowerCase().includes(query.toLowerCase()) ||
          p.email.toLowerCase().includes(query.toLowerCase()),
      )
    : persons

  function startEdit(email: string, initialMode: Mode) {
    setEditingEmail(email)
    setMode(initialMode)
    setQuery('')
    setSelectedPersonEmail('')
    setResponsibleQuery('')
    setSelectedResponsibleEmail('')
    setDepartment('')
    setComment('')
  }

  function cancelEdit() {
    setEditingEmail(null)
    setMode(null)
  }

  async function postLink(body: PersonLinkRequest) {
    await apiFetchJson('/api/person-links', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, serviceId }),
    }).catch(e => setError(String(e)))
    cancelEdit()
    load()
  }

  function extras() {
    return {
      responsibleEmail: selectedResponsibleEmail || undefined,
      department: department || undefined,
      comment: comment || undefined,
    }
  }

  async function clearOverride(email: string) {
    await apiFetchJson(`/api/person-links?email=${encodeURIComponent(email)}&serviceId=${encodeURIComponent(serviceId)}`, { method: 'DELETE' }).catch(e =>
      setError(String(e)),
    )
    load()
  }

  if (error) return <p>Error: {error}</p>
  if (!data) return <Spinner label="Loading user links..." />
  if (rows.length === 0) return <p>No assignments yet — run a sync first.</p>

  const info = mode ? MODE_INFO[mode] : null

  return (
    <>
      <ProblemFilterBar
        rows={rows}
        searchText={searchText}
        onSearchChange={setSearchText}
        problemFilters={problemFilters}
        problemFilter={problemFilter}
        onProblemFilterChange={setProblemFilter}
        roleFilter={roleFilter}
        onRoleFilterChange={setRoleFilter}
        hasRoles={hasRoles}
      />

      {visibleRows.length === 0 ? (
        <p>No rows match this filter.</p>
      ) : (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: 240 }}>Email (from service)</TableHeaderCell>
            <TableHeaderCell style={{ width: 140 }}>Resolution</TableHeaderCell>
            <TableHeaderCell>Person</TableHeaderCell>
            <TableHeaderCell style={{ width: 130 }}>Department</TableHeaderCell>
            <TableHeaderCell style={{ width: 170 }}>Responsible</TableHeaderCell>
            <TableHeaderCell style={{ width: 130 }}>Login type</TableHeaderCell>
            <TableHeaderCell style={{ width: 150 }}>Last login</TableHeaderCell>
            <TableHeaderCell style={{ width: 70 }}>Licenses</TableHeaderCell>
            {canManage && <TableHeaderCell style={{ width: 260 }}>Action</TableHeaderCell>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleRows.map(row => (
            <TableRow key={row.rawEmail}>
              <TableCell>
                <Tooltip content={row.rawEmail} relationship="label" withArrow>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.rawEmail}</div>
                </Tooltip>
              </TableCell>
              <TableCell>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                  <ResolutionBadge kind={row.resolutionKind} />
                  {isServiceBlocked(row) && (
                    <Tooltip
                      content={`Disabled in the vendor's own system (${row.accountStatus}) but still holds this license — a wasted seat.`}
                      relationship="description"
                      withArrow
                    >
                      <Lozenge color="danger">Blocked in service</Lozenge>
                    </Tooltip>
                  )}
                  {isMissingOwner(row) && (
                    <Tooltip
                      content="No department and no responsible person set — this account doesn't count as owned by anyone. Use Mark external/shared (or set a department in Entra) to fix it."
                      relationship="description"
                      withArrow
                    >
                      <Lozenge color="warning">No owner</Lozenge>
                    </Tooltip>
                  )}
                  {isNotSso(row) && (
                    <Tooltip
                      content={`Signs in via "${row.loginType}" rather than the org's SSO — bypasses centralized MFA/deprovisioning.`}
                      relationship="description"
                      withArrow
                    >
                      <Lozenge color="danger">Not SSO</Lozenge>
                    </Tooltip>
                  )}
                  {isStale(row, staleLoginDays) && (
                    <Tooltip
                      content={`No login in the last ${staleLoginDays} days${row.lastLoginAt ? ` (last: ${row.lastLoginAt})` : ' (never logged in)'} — a likely wasted seat.`}
                      relationship="description"
                      withArrow
                    >
                      <Lozenge color="warning">Stale login</Lozenge>
                    </Tooltip>
                  )}
                  {hasDuplicateLicense(row, duplicateKeys) && (
                    <Tooltip
                      content="This person holds the same license type more than once on this service (typically a primary email plus an alias) — a likely wasted/duplicate seat."
                      relationship="description"
                      withArrow
                    >
                      <Lozenge color="danger">Duplicate</Lozenge>
                    </Tooltip>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div>{row.personDisplayName}</div>
                {row.role && (
                  <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>{row.role}</div>
                )}
                {row.comment && (
                  <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>{row.comment}</div>
                )}
              </TableCell>
              <TableCell>{row.department ?? '—'}</TableCell>
              <TableCell>
                <div>{row.responsibleDisplayName ?? '—'}</div>
                {row.responsibleNeedsReview && (
                  <Tooltip
                    content="The named responsible person is now disabled or removed in Entra — this override needs to be revisited."
                    relationship="description"
                    withArrow
                  >
                    <Lozenge color="danger">Responsible blocked</Lozenge>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>{row.loginType ?? '—'}</TableCell>
              <TableCell>{row.lastLoginAt ? row.lastLoginAt.replace('T', ' ') : '—'}</TableCell>
              <TableCell>{(row.byService[serviceId] ?? []).filter(a => !a.excludeFromSeatCount).length}</TableCell>
              {canManage && (
                <TableCell>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'nowrap' }}>
                    <Tooltip content={MODE_INFO.link.buttonHint} relationship="description" withArrow>
                      <Button style={{ whiteSpace: 'nowrap' }} size="small" onClick={() => startEdit(row.rawEmail, 'link')}>
                        Link
                      </Button>
                    </Tooltip>
                    <Tooltip content={EXTERNAL_OR_SHARED_BUTTON_HINT} relationship="description" withArrow>
                      <Button style={{ whiteSpace: 'nowrap' }} size="small" onClick={() => startEdit(row.rawEmail, 'external')}>
                        Mark external/shared
                      </Button>
                    </Tooltip>
                    {(row.resolutionKind === 'manual' || row.resolutionKind === 'external' || row.resolutionKind === 'shared') && (
                      <Tooltip
                        content="Remove the manual link/external/shared override — resolution goes back to automatic Entra matching."
                        relationship="description"
                        withArrow
                      >
                        <Button style={{ whiteSpace: 'nowrap' }} size="small" onClick={() => clearOverride(row.rawEmail)}>
                          Clear override
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      )}

      <Dialog open={editingEmail !== null} onOpenChange={(_e, d) => !d.open && cancelEdit()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{info?.title} — {editingEmail}</DialogTitle>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: tokens.colorNeutralForeground3 }}>{info?.description}</p>
              {mode === 'link' && (
                <>
                  <Combobox
                    placeholder="Search person..."
                    value={query}
                    onChange={e => {
                      setQuery(e.target.value)
                      setSelectedPersonEmail('')
                    }}
                    onOptionSelect={(_e, d) => {
                      setSelectedPersonEmail(d.optionValue ?? '')
                      setQuery(d.optionText ?? '')
                    }}
                  >
                    {filteredPersons.map(p => (
                      <Option key={p.email} value={p.email} text={p.displayName}>
                        {p.displayName} ({p.email})
                      </Option>
                    ))}
                  </Combobox>
                  <Textarea placeholder="Comment (optional)" value={comment} onChange={(_e, d) => setComment(d.value)} />
                </>
              )}
              {(mode === 'external' || mode === 'shared') && (
                <RadioGroup
                  layout="horizontal"
                  value={mode}
                  onChange={(_e, d) => setMode(d.value as Mode)}
                >
                  <Radio value="external" label="External contact" />
                  <Radio value="shared" label="Shared / departmental" />
                </RadioGroup>
              )}
              {(mode === 'external' || mode === 'shared') && (
                <OverrideExtraFields
                  persons={persons}
                  departments={departments}
                  responsibleQuery={responsibleQuery}
                  onResponsibleQueryChange={setResponsibleQuery}
                  onResponsibleSelect={setSelectedResponsibleEmail}
                  department={department}
                  onDepartmentChange={setDepartment}
                  comment={comment}
                  onCommentChange={setComment}
                />
              )}
            </DialogContent>
            <DialogActions>
              {mode === 'link' && (
                <Button
                  appearance="primary"
                  disabled={!selectedPersonEmail}
                  onClick={() =>
                    editingEmail &&
                    postLink({ email: editingEmail, status: 'linked', personEmail: selectedPersonEmail, ...extras() })
                  }
                >
                  {info?.confirmLabel}
                </Button>
              )}
              {(mode === 'external' || mode === 'shared') && (
                <Button
                  appearance="primary"
                  onClick={() => editingEmail && postLink({ email: editingEmail, status: mode, ...extras() })}
                >
                  {info?.confirmLabel}
                </Button>
              )}
              <Button onClick={cancelEdit}>Cancel</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
